import * as THREE from 'three';
import type { ModuleTransformState, UniversalBundle } from '@/shared/api/types';

export function extractComposedModuleIds(bundle: UniversalBundle | null): Set<string> {
  const out = new Set<string>();
  const composition = bundle?.canonicalScene?.scene?.viewerHints?.moduleComposition;
  const instances = Array.isArray(composition?.instances) ? composition.instances : [];
  for (const item of instances) {
    const id = String(item?.instanceId || '').trim();
    if (id) out.add(id);
  }
  return out;
}

export function resolveGraphPoint(
  nodeId: string,
  moduleId: string | null | undefined,
  entityIndex: Map<string, THREE.Object3D>,
  modules: Array<{ id: string; center: [number, number, number] }>,
): THREE.Vector3 | null {
  const fromModule = moduleId ? modules.find((module) => module.id === moduleId) ?? null : null;
  if (fromModule) return new THREE.Vector3(...fromModule.center);

  const object = entityIndex.get(nodeId);
  if (object) {
    const point = new THREE.Vector3();
    object.getWorldPosition(point);
    return point;
  }

  const moduleFallback = modules.find((module) => module.id === nodeId);
  return moduleFallback ? new THREE.Vector3(...moduleFallback.center) : null;
}

export function resolveConflictPoint(
  nodeId: string,
  moduleId: string | null | undefined,
  markers: Array<{ moduleId: string; reason: string; position: [number, number, number] }>,
): THREE.Vector3 | null {
  const targetModuleId = moduleId || nodeId;
  const candidates = markers.filter((item) => item.moduleId === targetModuleId);
  if (!candidates.length) return null;
  const priority = (reason: string) => {
    if (reason === 'collision') return 0;
    if (reason === 'floor') return 1;
    if (reason === 'bounds') return 2;
    if (reason === 'scale') return 3;
    return 4;
  };
  candidates.sort((a, b) => priority(a.reason) - priority(b.reason));
  return new THREE.Vector3(...candidates[0]!.position);
}

export function easeInOutCubic(value: number): number {
  return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
}

export function clamp01(value: number): number {
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

export function clamp(value: number, minValue: number, maxValue: number): number {
  if (value < minValue) return minValue;
  if (value > maxValue) return maxValue;
  return value;
}

export function roundToMm(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export function extractModuleState(bundle: UniversalBundle): {
  transforms: Record<string, ModuleTransformState>;
  notes: Record<string, string>;
} | null {
  const scene = bundle.canonicalScene?.scene as Record<string, unknown> | undefined;
  const viewerHints = scene?.viewerHints as Record<string, unknown> | undefined;
  const moduleEdits = viewerHints?.moduleEdits as Record<string, unknown> | undefined;
  if (!moduleEdits) return null;

  const transforms: Record<string, ModuleTransformState> = {};
  const notes: Record<string, string> = {};
  const rawTransforms = moduleEdits.transforms;
  if (rawTransforms && typeof rawTransforms === 'object' && !Array.isArray(rawTransforms)) {
    for (const [moduleIdRaw, rawTransform] of Object.entries(rawTransforms)) {
      const moduleId = String(moduleIdRaw || '').trim();
      if (!moduleId || !rawTransform || typeof rawTransform !== 'object' || Array.isArray(rawTransform)) continue;
      const transform = rawTransform as Record<string, unknown>;
      const position = transform.position;
      const scale = transform.scale;
      if (!Array.isArray(position) || position.length < 3 || !Array.isArray(scale) || scale.length < 3) continue;
      const p: [number, number, number] = [Number(position[0]), 0, Number(position[2])];
      const s: [number, number, number] = [Number(scale[0]), Number(scale[1]), Number(scale[2])];
      if (!p.every(Number.isFinite) || !s.every((part) => Number.isFinite(part) && part > 0)) continue;
      transforms[moduleId] = { position: p, scale: s };
    }
  }

  const rawNotes = moduleEdits.notes;
  if (rawNotes && typeof rawNotes === 'object' && !Array.isArray(rawNotes)) {
    for (const [moduleIdRaw, noteRaw] of Object.entries(rawNotes)) {
      const moduleId = String(moduleIdRaw || '').trim();
      const note = String(noteRaw || '').trim();
      if (moduleId && note) notes[moduleId] = note;
    }
  }

  return { transforms, notes };
}
